import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { searchFoodsRemotely, type FoodSearchResult, type NutritionComponentEstimate, type NutritionRowEstimate, type ParsedVoiceMealDraft } from "../../application/remote-meal-parser";
import { SwipeDismissDialog } from "../../ui/components/SwipeDismissDialog";
import { FoodSettings, type FoodSettingsHandle } from "./FoodSettings";
import { isNutritionRowComplete, isNutritionRowSaveable } from "../../application/nutrition-completeness";

export function MealNutritionTable({ draft, onBlockingChange, onExpandedChange, onChange }: { draft: ParsedVoiceMealDraft; onBlockingChange: (open: boolean) => void; onExpandedChange: (open: boolean) => void; onChange: (draft: ParsedVoiceMealDraft) => void }) {
  const nutrition = { ...draft.nutrition!, rows: draft.nutrition!.rows.map(row => isNutritionRowSaveable(row) ? row : { ...row, calories: null, macrosUnknown: true }) };
  const incomplete = nutrition.rows.some(row => !isNutritionRowComplete(row));
  const blocked = nutrition.rows.some(row => !isNutritionRowSaveable(row));
  const swipeStart = useRef<{ rowIndex: number; pointerId: number; x: number; y: number; baseOffset: number; horizontal: boolean | null } | null>(null);
  const suppressRowClick = useRef(false);
  const [swipedRow, setSwipedRow] = useState<number | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [variantRow, setVariantRow] = useState<number | null>(null);
  const [variantComponent, setVariantComponent] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(() => new Set());
  const [variants, setVariants] = useState<FoodSearchResult[]>([]);
  const [variantStatus, setVariantStatus] = useState<"idle" | "loading" | "error">("idle");
  const [showCatalog, setShowCatalog] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [variantDraft, setVariantDraft] = useState<ParsedVoiceMealDraft | null>(null);
  const [settingsRevision, setSettingsRevision] = useState(0);
  const settingsRef = useRef<FoodSettingsHandle>(null);
  const searchRequest = useRef(0);
  const saving = useRef(false);
  useEffect(() => onExpandedChange(expandedRows.size > 0), [expandedRows, onExpandedChange]);
  const updateExpandedRow = (rowIndex: number, open: boolean) => {
    setExpandedRows(current => {
      const next = new Set(current);
      if (open) next.add(rowIndex);
      else next.delete(rowIndex);
      return next;
    });
  };
  const showVariants = async (rowIndex: number, componentIndex: number | null = null) => {
    const row = nutrition.rows[rowIndex]!;
    setVariantRow(rowIndex);
    setVariantDraft(draft);
    setVariantComponent(componentIndex);
    setShowCatalog(componentIndex !== null);
    onBlockingChange(true);
    setVariantStatus("loading");
    const request = ++searchRequest.current;
    try {
      const targetName = componentIndex === null ? row.name : row.components[componentIndex]?.name ?? row.name;
      const query = variantSearchQuery(targetName);
      const found = await searchFoodsRemotely(query);
      if (request !== searchRequest.current) return;
      setVariants(found);
      setVariantStatus("idle");
    } catch {
      if (request !== searchRequest.current) return;
      setVariants([]);
      setVariantStatus("error");
    }
  };
  const chooseVariant = (rowIndex: number, food: FoodSearchResult) => {
    if (settingsBusy || !variantDraft) return;
    const nutrition = variantDraft.nutrition!;
    const selected = food.nutrition;
    if (!selected) return;
    const currentRow = nutrition.rows[rowIndex]!;
    const currentGrams = variantComponent === null
      ? currentRow.components.reduce((sum, component) => sum + (component.grams ?? 0), 0)
      : currentRow.components[variantComponent]?.grams ?? 0;
    const targetGrams = currentGrams > 0 ? currentGrams : selected.grams;
    const factor = selected.grams > 0 ? targetGrams / selected.grams : 1;
    const scaled = (value: number | null) => value === null ? null : Math.round(value * factor * 10) / 10;
    const replacementComponent = {
      name: food.matchedName || food.name,
      amount: targetGrams,
      unit: "g",
      grams: targetGrams,
      calories: scaled(selected.calories),
      protein: scaled(selected.protein),
      fat: scaled(selected.fat),
      carbs: scaled(selected.carbs),
      valueSource: "estimated" as const,
    };
    const rows = nutrition.rows.map((row, index) => index !== rowIndex ? row : variantComponent === null ? {
      name: food.matchedName || food.name,
      portion: `${targetGrams} г`,
      calories: scaled(selected.calories),
      protein: scaled(selected.protein) ?? 0,
      fat: scaled(selected.fat) ?? 0,
      carbs: scaled(selected.carbs) ?? 0,
      valueSource: "estimated" as const,
      components: [replacementComponent],
      options: [], assumption: "По выбранному продукту из каталога.",
    } : recalculateNutritionRow(row, variantComponent, replacementComponent));
    const total = (field: "calories" | "protein" | "fat" | "carbs") => Math.round(rows.reduce((sum, row) => sum + (row[field] ?? 0), 0) * 10) / 10;
    const items = variantDraft.items.map((item, index) => {
      if (index !== rowIndex) return item;
      if (variantComponent === null) return { name: food.matchedName || food.name, sourceText: food.matchedName || food.name, amount: targetGrams, unit: "g" as const, components: [], ...(!food.isUserFood ? { catalogFoodId: selected.foodId } : {}) };
      const { foodSettings: _settings, catalogFoodId: _catalog, ...original } = item;
      return { ...original, customRecipe: true, components: currentRow.components.map((component, componentIndex) => componentIndex === variantComponent
        ? { name: food.matchedName || food.name, amount: targetGrams, unit: "g" as const, ...(!food.isUserFood ? { catalogFoodId: selected.foodId } : {}) }
        : { name: component.name, amount: component.grams, unit: "g" as const }) };
    });
    setVariantDraft({ ...variantDraft, items, nutrition: { rows, totalCalories: total("calories"), totalProtein: total("protein"), totalFat: total("fat"), totalCarbs: total("carbs") } });
    setSettingsRevision(value => value + 1);
    setShowCatalog(false);
  };
  const closeVariants = () => {
    if (settingsBusy) return;
    searchRequest.current++;
    setVariantDraft(null);
    setVariantRow(null);
    setVariantComponent(null);
    onBlockingChange(false);
  };
  const saveVariants = async () => {
    if (settingsBusy || saving.current || !variantDraft) return;
    saving.current = true;
    try {
      const updated = settingsRef.current ? await settingsRef.current.prepareSave() : variantDraft;
      if (!updated) return;
      onChange(updated);
      searchRequest.current++;
      setVariantDraft(null);
      setVariantRow(null);
      setVariantComponent(null);
      onBlockingChange(false);
    } finally { saving.current = false; }
  };
  const removeRow = (rowIndex: number) => {
    const rows = nutrition.rows.filter((_, index) => index !== rowIndex);
    const total = (field: "calories" | "protein" | "fat" | "carbs") => Math.round(rows.reduce((sum, row) => sum + (row[field] ?? 0), 0) * 10) / 10;
    onChange({
      ...draft,
      items: draft.items.filter((_, index) => index !== rowIndex),
      nutrition: { rows, totalCalories: total("calories"), totalProtein: total("protein"), totalFat: total("fat"), totalCarbs: total("carbs") },
    });
    setSwipedRow(null);
    setSwipeOffset(0);
  };
  const startSwipe = (rowIndex: number, event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const baseOffset = swipedRow === rowIndex ? 82 : 0;
    if (swipedRow !== null && swipedRow !== rowIndex) {
      setSwipedRow(null);
      setSwipeOffset(0);
    }
    swipeStart.current = { rowIndex, pointerId: event.pointerId, x: event.clientX, y: event.clientY, baseOffset, horizontal: null };
  };
  const moveSwipe = (rowIndex: number, event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = swipeStart.current;
    if (!gesture || gesture.rowIndex !== rowIndex || gesture.pointerId !== event.pointerId) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    if (gesture.horizontal === null && (Math.abs(dx) > 7 || Math.abs(dy) > 7)) gesture.horizontal = Math.abs(dx) > Math.abs(dy);
    if (!gesture.horizontal) return;
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    suppressRowClick.current = true;
    setSwipedRow(rowIndex);
    setSwipeOffset(Math.max(0, Math.min(82, gesture.baseOffset + dx)));
  };
  const finishSwipe = (rowIndex: number, event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = swipeStart.current;
    if (!gesture || gesture.rowIndex !== rowIndex || gesture.pointerId !== event.pointerId) return;
    swipeStart.current = null;
    if (gesture.horizontal) {
      const finalOffset = Math.max(0, Math.min(82, gesture.baseOffset + event.clientX - gesture.x));
      const shouldOpen = finalOffset >= 38;
      setSwipedRow(shouldOpen ? rowIndex : null);
      setSwipeOffset(shouldOpen ? 82 : 0);
      window.setTimeout(() => { suppressRowClick.current = false; }, 0);
    }
  };
  const closeOpenSwipe = () => {
    if (swipedRow === null) return;
    setSwipedRow(null);
    setSwipeOffset(0);
  };
  return <div className="nutrition-result" onScroll={closeOpenSwipe}>
    <section className="nutrition-total-card nutrition-total-top"><span>{incomplete ? "Расчёт неполный" : "Всего за приём пищи"}</span><strong>{blocked ? "—" : `≈ ${formatEnergy(nutrition.totalCalories)}`} <small>ккал</small></strong><div className="nutrition-total-macros"><span><i>Б</i><b>{blocked ? "—" : formatMacro(nutrition.totalProtein)}</b><small>белки</small></span><span><i>Ж</i><b>{blocked ? "—" : formatMacro(nutrition.totalFat)}</b><small>жиры</small></span><span><i>У</i><b>{blocked ? "—" : formatMacro(nutrition.totalCarbs)}</b><small>углеводы</small></span></div></section>
    {incomplete && !blocked && <p className="nutrition-hint" role="status">Пропущенные продукты не учтены. Сумма калорий и БЖУ неполная.</p>}
    {blocked && <p className="nutrition-hint" role="status">Не все компоненты посчитаны. Нажмите на компонент с прочерком и выберите аналог из каталога или укажите калорийность всего блюда.</p>}
    <div className="nutrition-list" aria-label="Распознанные блюда">
      {nutrition.rows.map((row, index) => <div className={`nutrition-swipe-row${swipedRow === index ? " is-active" : ""}`} key={`${row.name}-${index}`} onPointerDown={event => startSwipe(index, event)} onPointerMove={event => moveSwipe(index, event)} onPointerUp={event => finishSwipe(index, event)} onPointerCancel={event => finishSwipe(index, event)}>
        <button className="nutrition-delete" type="button" aria-label={`Удалить ${row.name}`} onPointerDown={event => event.stopPropagation()} onClick={() => removeRow(index)}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg><span>Удалить</span></button>
        <details className="nutrition-item" style={{ transform: `translateX(${swipedRow === index ? swipeOffset : 0}px)` }} onToggle={event => updateExpandedRow(index, event.currentTarget.open)} onClickCapture={event => { if (suppressRowClick.current) { event.preventDefault(); event.stopPropagation(); } }}>
        <summary><div><h3>{capitalize(row.name)}</h3></div><strong>{formatEnergy(row.calories)}<small> ккал</small></strong><svg className="nutrition-expand-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>{nutritionDescription(row) && <p>{nutritionDescription(row)}</p>}<small className="nutrition-portion">{row.isUserFood && <i className="user-food-dot" title="Мой продукт" aria-label="Мой продукт" />} {portionLabel(row)} · {row.kcalPortion != null ? "калории всей порции" : formatCaloriesPer100(row.calories, row.components.reduce((sum, component) => sum + (component.grams ?? 0), 0), row.portion?.includes("мл") ? "мл" : "г")}</small></summary>
        <div className="nutrition-item-details">
          {row.assumption ? <p className="nutrition-estimate-note">{row.assumption}</p> : row.valueSource === "estimated" && <p className="nutrition-estimate-note">Приблизительный расчёт по каталогу</p>}
          <p className="nutrition-section-label">Пищевая ценность</p>
          <div className="nutrition-expanded-macros"><span><b>Б</b>{row.macrosUnknown ? "—" : formatMacro(row.protein)}</span><span><b>Ж</b>{row.macrosUnknown ? "—" : formatMacro(row.fat)}</span><span><b>У</b>{row.macrosUnknown ? "—" : formatMacro(row.carbs)}</span></div>
          {row.components.length > 1 && <><p className="nutrition-section-label nutrition-composition-label">Состав <small>Нажмите на продукт, чтобы заменить</small></p><div className="nutrition-components">{row.components.map((component, componentIndex) => <button className="nutrition-component" type="button" key={`${component.name}-${componentIndex}`} onClick={() => void showVariants(index, componentIndex)}><strong>{capitalize(cleanCatalogName(component.name))}{isGenericCheese(component.name) && <small> · Уточнить вид сыра</small>}{component.ignored && <small> · Не учтён</small>}</strong><b>{formatEnergy(component.calories)} <small>ккал</small><i>›</i></b><span>{component.grams ?? "—"} г · {formatCaloriesPer100(component.calories, component.grams ?? 0)}</span><em>Б {formatMacroNumber(component.protein)} · Ж {formatMacroNumber(component.fat)} · У {formatMacroNumber(component.carbs)}</em></button>)}</div></>}
          <div className="nutrition-item-actions is-single-action"><button className="nutrition-variants-button" type="button" onClick={() => void showVariants(index)}>{(row.profileKey?.startsWith("cheese") || row.options?.some(option => option.key === "cheeseType")) ? "Уточнить вид сыра" : "Варианты и калорийность"}</button></div>
        </div>
        </details>
      </div>)}
      {nutrition.rows.length === 0 && <p className="nutrition-empty">Все продукты удалены</p>}
    </div>
    {variantRow !== null && variantDraft && <div className="popup-backdrop popup-backdrop--blocking" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) closeVariants(); }}>
      <SwipeDismissDialog className="variant-popup" role="dialog" aria-modal="true" aria-labelledby="variant-popup-title" onDismiss={closeVariants} onKeyDown={event => { if (event.key === "Escape") { event.stopPropagation(); closeVariants(); } }}>
        <header><div><span>{showCatalog ? "Выбор из каталога" : "Другие варианты"}</span><h2 id="variant-popup-title">{capitalize(variantComponent === null ? variantDraft.nutrition!.rows[variantRow]?.name ?? "Продукт" : cleanCatalogName(variantDraft.nutrition!.rows[variantRow]?.components[variantComponent]?.name ?? "Продукт"))}</h2></div><button type="button" disabled={settingsBusy} aria-label="Закрыть варианты" onClick={closeVariants}>×</button></header>
        <div className="variant-popup-content" hidden={showCatalog}>
          {variantComponent === null && <FoodSettings ref={settingsRef} key={`${variantRow}-${settingsRevision}`} draft={variantDraft} index={variantRow} onChange={setVariantDraft} onBusy={setSettingsBusy} />}
          {variantComponent !== null && <p className="variant-popup-hint">Выбранный продукт заменит ингредиент после сохранения. Вес порции сохранится.</p>}
          <button className="food-calorie-toggle" disabled={settingsBusy} type="button" onClick={() => setShowCatalog(true)}>Выбрать другой продукт из каталога</button>
        </div>
        {showCatalog && <div className="variant-popup-list" aria-live="polite">
          {variantStatus === "loading" && <p className="variant-popup-status">Ищу похожие продукты…</p>}
          {variantStatus === "error" && <p className="variant-popup-status">Не удалось загрузить варианты.</p>}
          {variantStatus === "idle" && variants.map(food => <button className="nutrition-variant" type="button" key={food.id} onClick={() => chooseVariant(variantRow, food)}><span><strong>{capitalize(cleanCatalogName(food.matchedName === "Сырники" ? food.name : food.matchedName || food.name))}</strong><small>{food.isUserFood && <i className="user-food-dot" aria-hidden="true" />} {food.isUserFood ? "Мой продукт" : "Значения на 100 г"}</small></span><b>{variantPer100(food.nutrition?.calories ?? null, food.nutrition?.grams ?? 0)} <small>ккал</small></b><em>Б {variantPer100(food.nutrition?.protein ?? null, food.nutrition?.grams ?? 0)} г · Ж {variantPer100(food.nutrition?.fat ?? null, food.nutrition?.grams ?? 0)} г · У {variantPer100(food.nutrition?.carbs ?? null, food.nutrition?.grams ?? 0)} г</em></button>)}
          {variantStatus === "idle" && variants.length === 0 && <p className="variant-popup-status">Других вариантов пока нет.</p>}
        </div>}
        <footer className="variant-popup-footer">
          {showCatalog ? <><p>Выберите продукт, затем сохраните изменения в настройках.</p><button type="button" className="variant-cancel" onClick={() => setShowCatalog(false)}>Назад к настройкам</button></> : <><button type="button" className="variant-cancel" disabled={settingsBusy} onClick={closeVariants}>Отмена</button><button type="button" className="variant-save" disabled={settingsBusy} onClick={() => void saveVariants()}>Сохранить изменения</button></>}
        </footer>
      </SwipeDismissDialog>
    </div>}
  </div>;
}

function cleanCatalogName(value: string): string {
  return value.replace(/\s+по\s+\d+(?:[-–—.]\d+)+(?:\s*[-–—]\s*\d+)?\s*$/iu, "").trim();
}

function variantSearchQuery(value: string): string {
  const normalized = value.toLocaleLowerCase("ru-RU").replace(/ё/gu, "е");
  if (/сырник/u.test(normalized)) return "сырники";
  if (/сосиск/u.test(normalized)) return "сосиски";
  if (/наггетс/u.test(normalized)) return "наггетсы";
  if (/сыр/u.test(normalized)) return "сыр";
  if (/масл/u.test(normalized)) return "масло";
  if (/хлеб|тост/u.test(normalized)) return "хлеб";
  if (/колбас/u.test(normalized)) return "колбаса";
  return cleanCatalogName(value);
}

function recalculateNutritionRow(row: NutritionRowEstimate, componentIndex: number, replacement: NutritionComponentEstimate): NutritionRowEstimate {
  const components = row.components.map((component, index) => index === componentIndex ? replacement : component);
  const sum = (field: "calories" | "protein" | "fat" | "carbs") => Math.round(components.reduce((total, component) => total + (component[field] ?? 0), 0) * 10) / 10;
  const grams = components.reduce((total, component) => total + (component.grams ?? 0), 0);
  const { profileKey: _profile, kcal100: _kcal, kcalPortion: _portionKcal, ...original } = row;
  const incomplete = components.some(component => component.calories === null);
  return { ...original, options: [], assumption: "Состав изменён вручную.", macrosUnknown: incomplete, portion: grams > 0 ? `${grams} г` : row.portion, calories: components.some(c => c.calories === null && !c.ignored) || components.every(c => c.calories === null) ? null : sum("calories"), protein: sum("protein"), fat: sum("fat"), carbs: sum("carbs"), components };
}

function nutritionDescription(row: NutritionRowEstimate): string {
  const ignored = row.components.filter(c => c.ignored && c.calories === null);
  if (ignored.length) return `Не учтено: ${ignored.map(c => c.name).join(", ")}. Итог неполный.`;
  if (row.kcalPortion != null) return "Калорийность всей порции указана вами";
  if (row.kcal100 != null) return "Калорийность на 100 г указана вами";
  if (row.profileKey?.startsWith("category:")) return "Типовая оценка · можно уточнить";
  if (row.profileKey) return row.components.length > 1 ? "Типовой состав · можно изменить" : "Типовой продукт";
  const components = row.components.map(component => cleanCatalogName(component.name)).filter(Boolean);
  const unique = [...new Set(components)];
  if (!unique.length) return "";
  const normalizedRow = comparableProductName(row.name);
  if (unique.length === 1) {
    const normalizedComponent = comparableProductName(unique[0]!);
    const rowWords = new Set(normalizedRow.split(" ").filter(word => word.length > 2));
    const componentWords = new Set(normalizedComponent.split(" ").filter(word => word.length > 2));
    const shared = [...rowWords].filter(word => componentWords.has(word));
    if (normalizedRow === normalizedComponent || shared.length >= Math.min(rowWords.size, componentWords.size)) return "";
  }
  return unique.map(capitalize).join(" · ");
}

function comparableProductName(value: string): string {
  return value.toLocaleLowerCase("ru-RU").replace(/ё/gu, "е").replace(/\bсыр\b/gu, "").replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}

function portionLabel(row: NutritionRowEstimate): string {
  const grams = row.components.reduce((sum, component) => sum + (component.grams ?? 0), 0);
  const pieceMatch = row.portion?.match(/^(\d+(?:[.,]\d+)?)\s+piece$/u);
  if (pieceMatch && grams > 0) {
    const count = Number(pieceMatch[1]!.replace(",", "."));
    const gramsPerPiece = count > 0 ? grams / count : 0;
    const formattedPieceWeight = Number.isInteger(gramsPerPiece) ? gramsPerPiece : Math.round(gramsPerPiece * 10) / 10;
    return `${count} шт. × ${formattedPieceWeight} г = ${grams} г`;
  }
  if (row.portion && row.portion !== "стандартная порция") return row.portion.replace("piece", "шт.").replace("portion", "порц.").replace(/\bcan\b/giu, "бан.").replace(/\bpack\b/giu, "уп.").replace(/\bglass\b/giu, "стак.").replace(/\bcup\b/giu, "чаш.").replace(/\bkg\b/gu, "кг").replace(/\bml\b/gu, "мл").replace(/\bl\b/gu, "л").replace(/\bg\b/gu, "г");
  return grams > 0 ? `${grams} г` : "Вес не определён";
}

function formatMacroValue(value: number | null): string {
  return value === null ? "—" : `${Math.round(value)} г`;
}

function formatMacroNumber(value: number | null): number | string {
  return value === null ? "—" : Math.round(value);
}

function formatEnergy(value: number | null): number | string {
  return value === null ? "—" : Math.round(value);
}

function formatCaloriesPer100(calories: number | null, grams: number, unit = "г"): string {
  if (calories === null || grams <= 0) return "— ккал / 100 г";
  return `${Math.round(calories / grams * 100)} ккал / 100 ${unit}`;
}

function variantPer100(value: number | null, grams: number): number | string {
  return value === null || grams <= 0 ? "—" : Math.round(value / grams * 100);
}

function capitalize(value: string): string {
  return value ? value[0]!.toLocaleUpperCase("ru-RU") + value.slice(1) : value;
}

function formatMacro(value: number): string {
  return `${Math.round(value)} г`;
}

function unitLabel(unit: string | null): string {
  if (unit === "can") return "бан.";
  if (unit === "pack") return "уп.";
  if (unit === "glass") return "стак.";
  if (unit === "cup") return "чаш.";
  if (unit === "piece") return "шт.";
  if (unit === "ml") return "мл";
  if (unit === "g") return "г";
  return unit ?? "";
}

function isGenericCheese(name: string): boolean {
  return /^(?:сыр|(?:тв[её]рдый|полутв[её]рдый)\s+сыр|сыр\s+(?:тв[её]рдый|полутв[её]рдый))$/iu.test(name.trim());
}
